import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function SecurityEvolutionSection() {
  const { securityProtocols, learningCycle } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeCycle, setActiveCycle] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => {
      setActiveCycle((p) => (p + 1) % learningCycle.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [visible, learningCycle.length]);

  const blueprintSpecs = [
    { val: "TB-CSPN", lbl: t("sec_bp_arch") },
    { val: "&lt;1s", lbl: t("sec_bp_latency") },
    { val: "ISO 8601", lbl: t("sec_bp_calendar") },
    { val: "Zero-PII", lbl: t("sec_bp_pii") },
  ];

  return (
    <section ref={ref} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("sec_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("sec_title_1")}
            <br />
            <span className="text-amber-500">{t("sec_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("sec_subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          <div
            className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <h3 className="text-xl font-bold text-stone-900 mb-6 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-900 text-amber-400">
                <i className="ri-shield-keyhole-line text-base" />
              </span>
              {t("sec_privacy_title")}
            </h3>

            <div className="space-y-4">
              {securityProtocols.map((item, i) => (
                <div
                  key={item.title}
                  className={`flex items-start gap-4 p-5 rounded-2xl border border-stone-100 hover:border-stone-200 bg-stone-50 hover:-translate-y-0.5 transition-all duration-300 cursor-default ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-stone-100 flex-shrink-0">
                    <i className={`${item.icon} text-stone-700 text-lg`} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm mb-1">{item.title}</p>
                    <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-5 bg-stone-900 rounded-2xl flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400/20 flex-shrink-0">
                <i className="ri-server-line text-amber-400 text-lg" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-0.5">{t("sec_residency_title")}</p>
                <p className="text-stone-400 text-xs">{t("sec_residency_desc")}</p>
              </div>
            </div>
          </div>

          <div
            className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            <h3 className="text-xl font-bold text-stone-900 mb-6 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-900 text-amber-400">
                <i className="ri-seedling-line text-base" />
              </span>
              {t("sec_learning_title")}
            </h3>

            <div className="relative mb-6">
              <div
                className="rounded-3xl overflow-hidden h-52 mb-4"
                style={{
                  backgroundImage: `url(https://public.readdy.ai/ai/img_res/038eb4b97f1d249451b516ad2070ccfe.jpg)`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="bg-stone-900/75 backdrop-blur-sm h-full flex items-center justify-center">
                  <div className="text-center px-8">
                    <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-2">{t("sec_learning_badge")}</p>
                    <p className="text-white text-lg font-bold leading-snug">
                      {t("sec_learning_quote")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {learningCycle.map((item, i) => {
                const isActive = activeCycle === i;
                return (
                  <button
                    key={item.phase}
                    onClick={() => setActiveCycle(i)}
                    className={`w-full text-left flex items-start gap-4 p-5 rounded-2xl border transition-all duration-400 cursor-pointer ${
                      isActive ? "border-amber-200 bg-amber-50" : "border-stone-100 bg-white hover:border-stone-200"
                    }`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 ${isActive ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-400"}`}>
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <div>
                      <p className={`font-bold text-sm mb-1 transition-colors duration-300 ${isActive ? "text-amber-700" : "text-stone-700"}`}>
                        {item.phase}
                      </p>
                      <p className={`text-xs leading-relaxed transition-colors duration-300 ${isActive ? "text-stone-600" : "text-stone-400"}`}>
                        {item.desc}
                      </p>
                    </div>
                    {isActive && (
                      <div className="ml-auto flex-shrink-0">
                        <span className="w-2 h-2 rounded-full bg-amber-500 block animate-pulse" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 p-5 border border-stone-100 rounded-2xl bg-stone-50">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-stone-400 text-lg flex-shrink-0 mt-0.5" />
                <p className="text-stone-500 text-xs leading-relaxed">
                  <strong className="text-stone-700">{t("sec_episodic_strong")}</strong>{" "}
                  {t("sec_episodic_note")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 rounded-3xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">{t("sec_blueprint_badge")}</p>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("sec_blueprint_title")}
              </h3>
              <p className="text-stone-400 leading-relaxed text-sm">
                {t("sec_blueprint_desc").split(t("sec_blueprint_strong"))[0]}
                <strong className="text-amber-300">{t("sec_blueprint_strong")}</strong>
                {t("sec_blueprint_desc").split(t("sec_blueprint_strong"))[1]}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {blueprintSpecs.map((item) => (
                <div key={item.lbl} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/8">
                  <span className="text-amber-400 font-black text-sm min-w-fit" dangerouslySetInnerHTML={{ __html: item.val }} />
                  <span className="text-stone-400 text-xs">{item.lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
